import Cookies from "@/helpers/cookies"

const COOKIE_NAME = "user_geo_location"
const EXPIRATION_IN_DAYS = 15

const setupGeolocation = async (): Promise<void> => {
  if (!Cookies.get(COOKIE_NAME)) {
    try {
      const position = await requestLocation()
      const { latitude, longitude } = position.coords
      const cookieValue = JSON.stringify({
        lat: latitude.toFixed(4),
        lng: longitude.toFixed(4)
      })

      setCookie(cookieValue)
    } catch {
      // For the passive, on-load feature, we fail silently without interrupting the user.
    }
  }

  await listenToGeoPermissionChanges()
}

const requestLocation = (): Promise<GeolocationPosition> => {
  return new Promise((resolve, reject) => {
    if (!window.navigator.geolocation) {
      return reject(new Error("Geolocation is not supported by this browser."))
    }

    window.navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      // Timeout includes user's response time to the browser's permission prompt.
      timeout: 15000,
      maximumAge: 0
    })
  })
}

const triggerGeolocationSearch = async (): Promise<{ lat: string; lng: string } | null> => {
  const locationCookie = Cookies.get(COOKIE_NAME)

  if (locationCookie) {
    try {
      return JSON.parse(locationCookie)
    } catch {
      Cookies.remove(COOKIE_NAME)

      return null
    }
  }

  try {
    const position = await requestLocation()
    const { latitude, longitude } = position.coords
    const coords = { lat: latitude.toFixed(4), lng: longitude.toFixed(4) }

    setCookie(JSON.stringify(coords))

    return coords
  } catch (error) {
    if (error instanceof GeolocationPositionError) {
      throw error
    }

    return null
  }
}

const listenToGeoPermissionChanges = async (): Promise<void> => {
  try {
    const status = await window.navigator.permissions?.query({ name: "geolocation" })

    status?.addEventListener("change", () => {
      if (status!.state === "denied") {
        Cookies.remove(COOKIE_NAME)
      }
    })
  } catch {
    // Ignore
  }
}

const setCookie = (value: string): void => {
  Cookies.setAtUserRequest(COOKIE_NAME, value, { expires: EXPIRATION_IN_DAYS })
}

export { setupGeolocation, triggerGeolocationSearch }
